import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Loader2, UtensilsCrossed } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useUserStore } from '../../stores/useUserStore';
import { useTableStatusStore } from '../../stores/useTableStatusStore';

export const ModernReleaseAllTablesButton = () => {
  const { translate } = useTranslation();
  const { user } = useUserStore();
  const {
    fetchOccupiedTables,
    isFetchingOccupiedTables,
    tableStatuses,
    fetchTableStatuses,
  } = useTableStatusStore();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [occupiedTables, setOccupiedTables] = useState<
    Array<{ id: string; tableNumber: number; orderId?: string }>
  >([]);

  // Check if there are any occupied tables
  const hasOccupiedTables = tableStatuses.some(
    (status) => status.orderId || status.isAvailable === false
  );

  // Fetch table statuses on mount and periodically to check for occupied tables
  useEffect(() => {
    fetchTableStatuses();
    // Refresh every 30 seconds to check for new occupied tables
    const interval = setInterval(() => {
      fetchTableStatuses();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTableStatuses]);

  // Check if user has admin or store manager role
  const isAdmin =
    user?.roles?.includes('sysAdmin') || user?.roles?.includes('admin');
  const isStoreManager = user?.roles?.includes('storeManager');

  if (!isAdmin && !isStoreManager) {
    return null;
  }

  const handleOpen = async () => {
    setOpen(true);

    try {
      // Fetch real occupied tables from the API
      const occupiedTablesData = await fetchOccupiedTables();
      setOccupiedTables(occupiedTablesData);
    } catch (error) {
      console.error('Error fetching occupied tables:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setOccupiedTables([]);
  };

  const handleBulkRelease = async () => {
    if (occupiedTables.length === 0) {
      console.log('No tables to release');
      return;
    }

    setLoading(true);

    try {
      // First, fetch current table statuses to get full objects
      await fetchTableStatuses();

      // Release all occupied tables using PUT with table status ID
      const { authenticatedFetch } = await import(
        '../../utils/authenticatedFetch'
      );

      const releasePromises = occupiedTables.map(async (table) => {
        if (!table.id) {
          console.warn(`Table ${table.tableNumber} has no ID, skipping`);
          return null;
        }

        try {
          // Find the full table status object from the store
          const fullTableStatus = tableStatuses.find(
            (ts) => ts.id === table.id || ts.tableNumber === table.tableNumber
          );

          if (!fullTableStatus) {
            console.warn(
              `Table ${table.tableNumber} not found in tableStatuses, trying to fetch it...`
            );

            // Try to fetch the table status directly from API
            try {
              const fetchResponse = await authenticatedFetch(
                `/api/table_statuses?filter=${encodeURIComponent(
                  JSON.stringify({ tableNumber: table.tableNumber })
                )}`
              );
              const fetchData = await fetchResponse.json();
              const fetchedStatus = Array.isArray(fetchData)
                ? fetchData.find(
                    (ts: any) => ts.tableNumber === table.tableNumber
                  )
                : (fetchData.data || []).find(
                    (ts: any) => ts.tableNumber === table.tableNumber
                  );

              if (!fetchedStatus) {
                console.error(
                  `Table ${table.tableNumber} not found in API response`
                );
                return { success: false, tableNumber: table.tableNumber };
              }

              // Use PUT to update with fetched status
              // Use null instead of undefined for MongoDB to properly clear the field
              const updatePayload = {
                ...fetchedStatus,
                orderId: null,
                isAvailable: true,
                lastOrderDate: null,
              };

              // Remove undefined/null fields that shouldn't be sent
              Object.keys(updatePayload).forEach((key) => {
                if (updatePayload[key] === undefined) {
                  delete updatePayload[key];
                }
              });

              const response = await authenticatedFetch(
                `/api/table_statuses/${fetchedStatus.id}`,
                {
                  method: 'PUT',
                  body: JSON.stringify(updatePayload),
                }
              );

              if (!response.ok) {
                const errorText = await response.text();
                console.error(
                  `Failed to release table ${table.tableNumber}:`,
                  response.status,
                  errorText
                );
                return { success: false, tableNumber: table.tableNumber };
              }

              console.log(`Successfully released table ${table.tableNumber}`);
              return { success: true, tableNumber: table.tableNumber };
            } catch (fetchError) {
              console.error(
                `Error fetching/releasing table ${table.tableNumber}:`,
                fetchError
              );
              return {
                success: false,
                tableNumber: table.tableNumber,
                error: fetchError,
              };
            }
          }

          // Prepare update payload (like backend does)
          // Use null instead of undefined because JSON.stringify removes undefined values
          // MongoDB will properly clear fields set to null
          const updatePayload: any = {
            ...fullTableStatus,
            orderId: null,
            isAvailable: true,
            lastOrderDate: null,
          };

          // Remove undefined fields (but keep null as it's needed to clear MongoDB fields)
          Object.keys(updatePayload).forEach((key) => {
            if (updatePayload[key] === undefined) {
              delete updatePayload[key];
            }
          });

          console.log(
            `[Release Table ${table.tableNumber}] Updating with payload:`,
            updatePayload
          );

          // Use PUT to update the existing table status with full object (like backend does)
          const response = await authenticatedFetch(
            `/api/table_statuses/${table.id}`,
            {
              method: 'PUT',
              body: JSON.stringify(updatePayload),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.error(
              `[Release Table ${table.tableNumber}] Failed:`,
              response.status,
              response.statusText,
              errorText
            );
            return { success: false, tableNumber: table.tableNumber };
          }

          const responseData = await response.json();
          console.log(
            `[Release Table ${table.tableNumber}] Success:`,
            responseData
          );

          console.log(`Successfully released table ${table.tableNumber}`);
          return { success: true, tableNumber: table.tableNumber };
        } catch (error) {
          console.error(`Error releasing table ${table.tableNumber}:`, error);
          return { success: false, tableNumber: table.tableNumber, error };
        }
      });

      const results = await Promise.all(releasePromises);
      const successCount = results.filter((r) => r?.success).length;
      const failCount = results.length - successCount;

      // Refresh table statuses after releasing
      await fetchTableStatuses();

      console.log(
        `Release complete: ${successCount} succeeded, ${failCount} failed out of ${occupiedTables.length} tables`
      );

      if (successCount > 0) {
        handleClose();
      }
    } catch (error) {
      console.error('Error releasing tables:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="outline"
        disabled={!hasOccupiedTables}
        className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <UtensilsCrossed className="h-4 w-4 mr-2" />
        {translate('pos.orders.actions.release_all_tables')}
      </Button>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {translate('pos.orders.actions.release_all_tables_title')}
            </DialogTitle>
            <DialogDescription>
              {translate('pos.orders.actions.release_all_tables_message')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {loading ? (
              <div className="flex justify-center p-6">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <>
                {occupiedTables.length === 0 ? (
                  <div className="text-center py-4">
                    <Badge
                      variant="outline"
                      className="text-green-600 border-green-600"
                    >
                      {translate('pos.orders.actions.no_occupied_tables')}
                    </Badge>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <Badge
                        variant="outline"
                        className="text-orange-600 border-orange-600"
                      >
                        {translate(
                          'pos.orders.actions.occupied_tables_count',
                          {
                            count: occupiedTables.length,
                          }
                        )}
                      </Badge>
                    </div>
                    <div className="max-h-[240px] overflow-y-auto space-y-2">
                      {occupiedTables.map((table) => (
                        <div
                          key={table.tableNumber}
                          className="flex flex-col py-2 border-b last:border-0"
                        >
                          <p className="font-medium">
                            Table {table.tableNumber}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {table.orderId
                              ? `Order: ${table.orderId}`
                              : 'No active order'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              {translate('pos.common.action.cancel')}
            </Button>
            <Button
              onClick={handleBulkRelease}
              variant="destructive"
              disabled={loading || occupiedTables.length === 0}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {translate('pos.orders.actions.releasing')}
                </>
              ) : (
                translate('pos.orders.actions.confirm_release_all')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
